import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import {
  CharacterCardCore,
  Core,
  CoreWithLatestContribution,
  Virtual,
  VirtualStatsDTO,
} from '../../interfaces';
import CoreSummary from './CoreSummary';
import { IconPlug } from '@tabler/icons-react';
import cores from '../../shared/constants/Cores';
import { characterCardStyle } from './CharacterCard.style';
import VirtualCategory from './VirtualCategory';
import { formatNumber } from '../../shared/utils/Formatter';
import { StakeVirtual } from '../../interfaces/Stake';

interface CharacterCardProps extends Virtual {
  onClick: () => void;
  type: string;
  cores: CoreWithLatestContribution[];
  id: number;
  isAuth: boolean;
  stats: VirtualStatsDTO;
  stake?: StakeVirtual;
  revenue?: number;
}

interface CoreDTO extends CoreWithLatestContribution {
  icon: React.ReactElement;
}

// const PercentageIcon: React.FC<{
//   percentage: number;
//   icon: any;
// }> = ({ percentage, icon }) => {
//   return (
//     <Grid item xs={2.4} sx={{ alignItems: 'center', height: 'max-content' }}>
//       <Box sx={{ m: 1, position: 'relative' }}>
//         <Box
//           sx={{
//             position: 'absolute',
//             margin: 'auto',
//             left: 0,
//             right: 0,
//             top: 0,
//             bottom: 0,
//             width: '40px',
//             height: '40px',
//           }}
//         >
//           {icon}
//         </Box>
//         <CircularProgress
//           variant="determinate"
//           style={{ opacity: 0.2, position: 'absolute', zIndex: 2 }}
//           value={100}
//         />
//         <CircularProgress
//           variant="determinate"
//           style={{ position: 'absolute', zIndex: 1 }}
//           value={percentage}
//         />
//       </Box>
//     </Grid>
//   );
// };

const CharacterCard: React.FC<CharacterCardProps> = (props) => {
  const profileImage = props.image
    ? props.image.url
    : 'https://placehold.co/600x400';

  const virtualCores = useMemo(() => {
    if (!props.cores) {
      return [];
    }

    return props.cores.map((core: CharacterCardCore) => {
      const type =
        (core?.name?.replace(' ', '_')?.toUpperCase() as string) || null;
      if (!type) return null;

      Object.entries(cores).map(([key, stat]) => {
        if (key === type) {
          core.icon = stat.icon;
        }
        return true;
      });
      return core;
    });
  }, [props.cores]);

  return (
    <Paper
      sx={{
        ...characterCardStyle.cardStyle,
        border: props.type === 'contribution' ? '1px solid #F5F5F5' : '',
      }}
      className={`virtual-${props?.category
        ?.replaceAll(' ', '-')
        .toLowerCase()}`}
      onClick={props.onClick}
    >
      <Box padding={2}>
        <Box display="flex" justifyContent="space-between" marginBottom={2}>
          {props.type === 'staker' ? (
            <Box
              display="flex"
              gap={0.5}
              alignItems="center"
              bgcolor="transparent"
              whiteSpace="nowrap"
              paddingX={1}
              borderRadius={50}
            >
              {''}
              {/*   <strong>28k $V</strong>{' '}
            <Typography display="inline">paidout</Typography> */}
            </Box>
          ) : props.type === 'withdraw' ? (
            <Box
              display="flex"
              gap={0.5}
              alignItems="center"
              flexDirection={'column'}
              bgcolor="#F5F5F5"
              whiteSpace="nowrap"
              paddingX={1}
              borderRadius={50}
            >
              {/* <strong>{(props.apr ? props.apr : 0) + '%'}</strong>{' '} */}
              <strong>
                {props.revenue && props.revenue > 1 && isFinite(props.revenue)
                  ? props.revenue.toFixed(2)
                  : 0}
                %{' '}
              </strong>{' '}
              <Typography display="inline">APR</Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              gap={0.5}
              alignItems="center"
              bgcolor="#F5F5F5"
              whiteSpace="nowrap"
              paddingX={1}
              borderRadius={50}
            >
              <strong>{props.stats.contributionsCount}</strong>{' '}
              <Typography display="inline">Contributions</Typography>
            </Box>
          )}
          <Box
            display="flex"
            gap={0.5}
            alignItems="center"
            bgcolor="#F5F5F5"
            whiteSpace="nowrap"
            paddingX={1}
            borderRadius={50}
          >
            <strong>{props?.stats?.dappCount ?? 0}</strong>{' '}
            <Typography display="inline">Dapps</Typography>
            <IconPlug size={15} />
          </Box>
        </Box>
        <img
          style={{
            ...characterCardStyle.profilePhotoStyle,
            objectFit: 'cover',
          }}
          src={profileImage}
          alt="virtual character"
        />
        <Typography
          sx={{
            ...characterCardStyle.virtualNameStyle,
          }}
          noWrap
        >
          {props.name}
        </Typography>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <VirtualCategory category={props.category} showTooltip={true} />
        </Box>
        {(props.type === 'staker' || props.type === 'withdraw') && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              justifyContent="center"
              marginTop={1}
              marginBottom={0.5}
              className="category-color"
            >
              <Typography fontSize={22} fontWeight="bold">
                {props?.stats?.totalStakeAmount < 1
                  ? 0
                  : formatNumber(props?.stats?.totalStakeAmount, 0, 0)}
              </Typography>
              {/*  <Typography fontSize={14}>(1.2%)</Typography> */}
            </Box>
            <Box
              sx={characterCardStyle.totalContributorsStyle}
              className="category-light"
            >
              <Typography fontWeight="bold" fontSize={12}>
                TOTAL $xVIRTUAL LOCKED
              </Typography>
            </Box>
          </>
        )}
        {props.type === 'contribution' && (
          <Box sx={characterCardStyle.coreListStyle}>
            {virtualCores.map(
              (stat: CharacterCardCore | null, index: number) => {
                return (
                  <CoreSummary
                    key={index}
                    icon={stat?.icon}
                    name={
                      stat?.latestContribution?.metadata?.packageName || 'N/A'
                    }
                    maturity={stat?.latestContribution?.maturity || 0}
                    totalContributions={0}
                  />
                );
              }
            )}
          </Box>
        )}
        {/* {props.type === 'withdraw' && (
        <Grid container spacing={3} sx={{ height: '100px', mt: 0.5 }}>
          <Grid item xs={12}>
            <Grid item container spacing={3}>
              {virtualCores.map((stat: CoreDTO, index: number) => {
                return (
                  <PercentageIcon
                    key={index}
                    icon={stat.icon}
                    percentage={stat.latestContribution?.maturity || 0}
                  />
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      )} */}
        {/* {props.type === 'withdraw' && (
        <Box sx={characterCardStyle.aprStyle}>
          <Box paddingY={0.5}>
            <Typography fontSize={16} fontWeight="bold" lineHeight={1}>
              {(props.stats.totalStakeAmount
                ? Math.round(props.stats.totalStakeAmount)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : 0) + ' $V'}
            </Typography>
            <Typography fontSize={12}>Staked</Typography>
          </Box>
        </Box>
      )}  */}
      </Box>

      <Box padding={props.type === 'contribution' ? 0 : '0 1rem 1rem 1rem'}>
        <Box
          sx={{
            ...characterCardStyle.aprStyle,
            borderRadius: props.type === 'contribution' ? 0 : '50px',
          }}
          display="flex"
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          paddingY={props.type === 'contribution' ? 2 : 0.5}
          paddingX={props.type === 'contribution' ? 2 : 0}
        >
          <Stack
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            gap={0.5}
          >
            {!props.isAuth && <Typography fontSize={12}>Up to</Typography>}
            <Typography fontSize={16} fontWeight="bold" lineHeight={1}>
              {props.revenue && props.revenue > 1 && isFinite(props.revenue)
                ? `${props.revenue.toFixed(2)}%`
                : `Coming Soon`}
            </Typography>
          </Stack>
          <Typography fontSize={12}>APR</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default CharacterCard;
